import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AgentIcon from '../components/custom-components/icons/AgentIcon';
import CallActivityIcon from '../components/custom-components/icons/CallActivityIcon';
import ProfileIcon from '../components/custom-components/icons/ProfileIcon';
import SubscriptionIcon from '../components/custom-components/icons/SubscriptionIcon';
import { PATHS } from '../router/routes';
import { Box, Divider, Text } from '@chakra-ui/react';
import format from 'date-fns/format';
import UpSortIcon from '../components/custom-components/icons/UpSortIcon';
import DownSortIcon from '../components/custom-components/icons/DownSortIcon';
import UnsortIcon from '../components/custom-components/icons/UnsortIcon';
import { useLocation } from 'react-router-dom';
import AutomationTemplateIcon from '../components/custom-components/icons/AutomationTemplateIcon';
import Education from '../components/custom-components/icons/Education';
import EmailNotifications from '../components/page-components/actions/EmailNotifications';
import TextNotifications from '../components/page-components/actions/TextNotifications';
import CallTransferSwitch from '../components/page-components/actions/CallTransferSwitch';
import TerminateCallActions from '../components/page-components/actions/TerminateCallActions';
import FillAudio from '../components/page-components/actions/FillAudio';

export function useGetMenuIcons(checkCurrentPage, isMobile = false, isTablet) {
    if (isMobile) {
        const menu = [
            {
                name: 'My Agent',
                icon: (
                    <AgentIcon
                        isTablet={isTablet}
                        isMobile={isMobile}
                        isPicked={checkCurrentPage(PATHS.MY_AGENT, PATHS.TRAINING_GUIDE)}
                    />
                ),
                path: PATHS.MY_AGENT,
                secondPath: PATHS.TRAINING_GUIDE,
            },
            {
                name: 'Call Activity',
                icon: (
                    <CallActivityIcon
                        isTablet={isTablet}
                        isMobile={isMobile}
                        isPicked={checkCurrentPage(PATHS.CALL_ACTIVITY)}
                    />
                ),
                path: PATHS.CALL_ACTIVITY,
            },

            {
                name: 'Profile',
                icon: (
                    <ProfileIcon
                        isTablet={isTablet}
                        isMobile={isMobile}
                        isPicked={checkCurrentPage(
                            PATHS.PROFILE_INFO,
                            PATHS.PROFILE_PASSWORD,
                            PATHS.CHANGE_INFO,
                            PATHS.PASSWORD
                        )}
                    />
                ),
                path: PATHS.PROFILE_INFO,
                secondPath: PATHS.PROFILE_PASSWORD,
                thirdPath: PATHS.CHANGE_INFO,
                fourthPath: PATHS.PASSWORD,
            },
            {
                name: 'Integrations',
                icon: (
                    <AutomationTemplateIcon
                        isMobile={isMobile}
                        isTablet={isTablet}
                        isPicked={checkCurrentPage(PATHS.AUTOMATION_TEMPLATE)}
                    />
                ),
                path: PATHS.AUTOMATION_TEMPLATE,
            },
            {
                name: 'Subscription',
                icon: (
                    <SubscriptionIcon
                        isTablet={isTablet}
                        isMobile={isMobile}
                        isPicked={checkCurrentPage(PATHS.SUBSCRIPTION, PATHS.ADD_MINUTES)}
                    />
                ),
                path: PATHS.SUBSCRIPTION,
                secondPath: PATHS.ADD_MINUTES,
            },
            {
                name: 'Help',
                icon: <Education />,
            },
        ];

        return isMobile && isTablet ? menu : menu.slice(0, -1);
    } else {
        return [
            {
                name: 'My Agent',
                icon: <AgentIcon mr='10px' isPicked={checkCurrentPage(PATHS.MY_AGENT, PATHS.KNOWLEDGE_BASE)} />,
                path: PATHS.MY_AGENT,
                secondPath: PATHS.KNOWLEDGE_BASE,
            },
            {
                name: 'Call Activity',
                icon: <CallActivityIcon mr='10px' isPicked={checkCurrentPage(PATHS.CALL_ACTIVITY)} />,
                path: PATHS.CALL_ACTIVITY,
            },
            {
                name: 'Integrations',
                icon: <AutomationTemplateIcon mr='10px' isPicked={checkCurrentPage(PATHS.AUTOMATION_TEMPLATE)} />,
                path: PATHS.AUTOMATION_TEMPLATE,
            },
            {
                name: 'Profile',
                icon: (
                    <ProfileIcon
                        mr='10px'
                        isPicked={checkCurrentPage(PATHS.PROFILE_INFO, PATHS.PROFILE_PASSWORD, PATHS.API_KEY)}
                    />
                ),
                path: PATHS.PROFILE_INFO,
                secondPath: PATHS.PROFILE_PASSWORD,
                thirdPath: PATHS.API_KEY,
            },
            {
                name: 'Subscription',
                icon: <SubscriptionIcon mr='10px' isPicked={checkCurrentPage(PATHS.SUBSCRIPTION)} />,
                path: PATHS.SUBSCRIPTION,
            },
        ];
    }
}

export function useGetFillIcon(isMobile, isPicked, isTablet) {
    if (isTablet && !isPicked) {
        return 'white';
    } else if (isMobile && isPicked) {
        return '#5611F8';
    } else if (isMobile && !isPicked) {
        return '#2D3748';
    } else if (!isMobile && isPicked) {
        return 'white';
    } else {
        return 'black';
    }
}

export function useIsMobile() {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        }
    }, []);

    return isMobile;
}

export const useSaveToClipBoard = (key, func, variant) => {
    return function () {
        navigator.clipboard
            .writeText(key)
            .then(() =>
                func({
                    status: 'success',
                    title: `${variant} saved in clipboard`,
                    isClosable: true,
                })
            )
            .catch((err) =>
                func({
                    status: 'error',
                    title: err.message,
                    isClosable: true,
                })
            );
    };
};

export const useCreateScript = () => {
    return function (content) {
        const script = document.createElement('script');
        script.textContent = content;
        script.async = true;
        return script;
    };
};

export const useGetActionsData = (agent) => {
    const actionsData = useMemo(() => {
        if (agent) {
            const terminateCallData = agent?.terminateCallAction;
            const callTransferData = {
                callTransfers: [...agent?.callTransfer],
                active: agent?.callTransferActive,
            };
            const textNotificationData = agent?.textNotification;
            const emailNotificationData = agent?.emailNotification;
            const fillerAudioData = agent?.fillerAudio;
            const isPremium = agent?.userSubscriptionPremium;
            const callService = agent?.callService;

            return {
                callTransferData,
                terminateCallData,
                textNotificationData,
                emailNotificationData,
                fillerAudioData,
                isPremium,
                callService,
            };
        }

        return { error: true };
    }, [agent]);

    return actionsData;
};

export const useGetActions = (actionsData, agentId, myEmail) => {
    const {
        callTransferData,
        terminateCallData,
        textNotificationData,
        emailNotificationData,
        fillerAudioData,
        isPremium,
        callService,
    } = actionsData;

    const actions = actionsData?.error
        ? []
        : [
            { component: CallTransferSwitch, data: { ...callTransferData, callService } },
            { component: EmailNotifications, data: emailNotificationData },
            { component: TextNotifications, data: textNotificationData },
            { component: TerminateCallActions, data: terminateCallData },
            { component: FillAudio, data: fillerAudioData, isLast: true },
            // { component: WebHook, isLast: true },
            //   { component: GoogleCalendar, isLast: true },
        ];

    return actions.map((action) => (
        <Box key={action}>
            <action.component isPremium={isPremium} myEmail={myEmail} agentId={agentId} data={action?.data} />
            {!action?.isLast && <Divider />}
        </Box>
    ));
};

export function getFormatedPath(agentId) {
    return (path) => `${path}/`.replace(':id?/', agentId);
}

export function useCloseEvent(clickState) {
    const onClickHandler = useCallback(() => {
        clickState(false);
    }, [clickState]);

    useEffect(() => {
        window.addEventListener('click', onClickHandler);
        return () => {
            window.removeEventListener('click', onClickHandler);
        };
    }, [onClickHandler]);
}

export function useCloseBurger(clickState) {
    const location = useLocation();

    const { pathname } = location;

    const onClickHandler = useCallback(() => {
        if (
            (pathname === PATHS.PROFILE_INFO || pathname === PATHS.PROFILE_PASSWORD) &&
            sessionStorage.getItem('menu') === 'close'
        ) {
            return;
        }

        if (sessionStorage.getItem('menu') === 'open') {
            clickState(false);
            sessionStorage.setItem('menu', 'close');
        } else if (!sessionStorage.getItem('menu')) {
            clickState(null);
        } else if (sessionStorage.getItem('menu') === 'close') {
            clickState(null);
            sessionStorage.removeItem('menu');
        }
    }, [clickState, pathname]);

    useEffect(() => {
        window.addEventListener('click', onClickHandler);
        return () => {
            window.removeEventListener('click', onClickHandler);
        };
    }, [onClickHandler]);
}

export function useGetDate() {
    const today = new Date();
    const startToday = new Date(today);
    const endToday = new Date(today);

    endToday.setHours(23, 59, 59, 999);
    startToday.setHours(0, 0, 0, 0);

    const startOfToday = startToday.toISOString().slice(0, -1);
    const endOfToday = endToday.toISOString().slice(0, -1);

    //-------------------------------------------------------

    const yesterday = new Date(today.getTime() - 86400000);
    const startYesterday = new Date(yesterday);
    const endYesterday = new Date(yesterday);

    endYesterday.setHours(23, 59, 59, 999);
    startYesterday.setHours(0, 0, 0, 0);

    const startOfYesterday = startYesterday.toISOString().slice(0, -1);
    const endOfYesterday = endYesterday.toISOString().slice(0, -1);

    //--------------------------------------------------------

    const currentDayOfWeek = today.getDay();

    const daysToMonday = currentDayOfWeek === 1 ? 0 : currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

    const startWeek = new Date(today.getTime() - daysToMonday * 86400000);

    startWeek.setHours(0, 0, 0, 0);

    const daysToEndOfWeek = 6 - currentDayOfWeek + 1;

    const endWeek = new Date(today.getTime() + daysToEndOfWeek * 86400000);

    endWeek.setUTCHours(23, 59, 59, 999);

    const startOfWeek = startWeek.toISOString().slice(0, -1);
    const endOfWeek = endWeek.toISOString().slice(0, -1);

    //--------------------------------------------------------

    const lastWeekDate = new Date(today.getTime() - 7 * 86400000);

    const lastWeekDayOfWeek = lastWeekDate.getDay();

    const daysToLastWeekMonday = lastWeekDayOfWeek === 1 ? 0 : lastWeekDayOfWeek === 0 ? 6 : lastWeekDayOfWeek - 1;

    const startLastWeek = new Date(lastWeekDate.getTime() - daysToLastWeekMonday * 86400000);

    startLastWeek.setHours(0, 0, 0, 0);

    const daysToEndOfLastWeek = 6 - lastWeekDayOfWeek + 1;

    const endLastWeek = new Date(lastWeekDate.getTime() + daysToEndOfLastWeek * 86400000);

    endLastWeek.setHours(23, 59, 59, 999);

    const startOfLastWeek = startLastWeek.toISOString().slice(0, -1);
    const endOfLastWeek = endLastWeek.toISOString().slice(0, -1);

    //------------------------------------------------------

    const currentDate = new Date();

    currentDate.setDate(1);
    currentDate.setHours(0, 0, 0, 0);

    const nextMonth = currentDate.getMonth() + 1;

    const startOfNextMonth = new Date(currentDate.getFullYear(), nextMonth, 1);

    const endMonth = new Date(startOfNextMonth.getTime() - 1);
    endMonth.setHours(23, 59, 59, 999);

    const startOfMonth = currentDate.toISOString().slice(0, -1);
    const endOfMonth = endMonth.toISOString().slice(0, -1);

    //------------------------------------------------------

    const initialDay = new Date();

    initialDay.setMonth(initialDay.getMonth() - 1);
    initialDay.setDate(1);
    initialDay.setHours(0, 0, 0, 0);

    const lastNextMonth = initialDay.getMonth() + 1;

    const startLastNextMonth = new Date(initialDay.getFullYear(), lastNextMonth, 1);

    const endLastNextMonth = new Date(startLastNextMonth.getTime() - 1);
    endLastNextMonth.setHours(23, 59, 59, 999);

    const startOfLastMonth = initialDay.toISOString().slice(0, -1);
    const endOfLastMonth = endLastNextMonth.toISOString().slice(0, -1);

    return {
        today: {
            startOfToday,
            endOfToday,
        },
        yesterday: {
            startOfYesterday,
            endOfYesterday,
        },
        thisWeek: {
            startOfWeek,
            endOfWeek,
        },
        lastWeek: {
            startOfLastWeek,
            endOfLastWeek,
        },
        thisMonth: {
            startOfMonth,
            endOfMonth,
        },
        lastMonth: {
            startOfLastMonth,
            endOfLastMonth,
        },
    };
}

export function useGetFormatedDate(firstDate, secondDate) {
    const result = useMemo(() => {
        if (firstDate && secondDate) {
            const firstDay = new Date(firstDate);
            const lastDay = new Date(secondDate);

            lastDay.setHours(23, 59, 59, 999);
            firstDay.setHours(0, 0, 0, 0);

            const startOfSelectedDate = firstDay.toISOString().slice(0, -1);
            const endOfSelectedDate = lastDay.toISOString().slice(0, -1);

            return {
                startOfSelectedDate,
                endOfSelectedDate,
            };
        }

        if (firstDate) {
            const firstDay = new Date(firstDate);
            const lastDay = new Date(firstDay);

            lastDay.setHours(23, 59, 59, 999);
            firstDay.setHours(0, 0, 0, 0);

            const startOfSelectedDate = firstDay.toISOString().slice(0, -1);
            const endOfSelectedDate = lastDay.toISOString().slice(0, -1);

            return {
                startOfSelectedDate,
                endOfSelectedDate,
            };
        }
    }, [firstDate, secondDate]);

    return result;
}

export function useGetSelectText(firstDate, secondDate, handler, isMobile = false) {
    const desktopVersion = useMemo(() => {
        if (!firstDate && !secondDate) {
            return <Text onClick={handler}>Select period</Text>;
        }

        if (firstDate === secondDate) {
            return <Text onClick={handler}>{`${format(new Date(firstDate), 'MMMM dd yyyy')}`}</Text>;
        }

        if (firstDate && secondDate) {
            return (
                <Text onClick={handler}>{`${format(new Date(firstDate), 'MMMM dd')} - ${format(
                    new Date(secondDate),
                    'MMMM dd yyyy'
                )}`}</Text>
            );
        }
    }, [firstDate, secondDate, handler]);

    const mobileVersion = useMemo(() => {
        if (firstDate === secondDate) {
            return `${firstDate && format(new Date(firstDate), 'MMMM dd yyyy')}`;
        }

        if (firstDate && secondDate) {
            if (
                format(new Date(firstDate), 'MMMM') === format(new Date(secondDate), 'MMMM') &&
                format(new Date(firstDate), 'yyyy') === format(new Date(secondDate), 'yyyy')
            ) {
                return `${format(new Date(firstDate), 'MMMM, dd')}-${format(new Date(secondDate), 'dd')}, ${format(
                    new Date(firstDate),
                    'yyyy'
                )}`;
            } else {
                return `${format(new Date(firstDate), 'MMM')}-${format(new Date(secondDate), 'MMM')}, ${format(
                    new Date(firstDate),
                    'dd'
                )}-${format(new Date(secondDate), 'dd')}, ${format(new Date(firstDate), 'yyyy')}`;
            }
        }
    }, [firstDate, secondDate]);

    if (isMobile) {
        return mobileVersion;
    } else {
        return desktopVersion;
    }
}

export function useGetThNames(filters, setFilters, isMobile = false) {
    const getIcon = (firstFilter, secondFilter) => {
        if (firstFilter && secondFilter) {
            return <UpSortIcon />;
        } else if (firstFilter && !secondFilter) {
            return <DownSortIcon />;
        } else {
            return <UnsortIcon />;
        }
    };

    const desktopVersion = [
        { name: '' },
        {
            name: 'DATE',
            canSelected: true,
            isSortible: true,
            icon: getIcon(filters.filterDate, filters.dateAsc),
            changeFilters() {
                if (filters.filterDate && filters.dateAsc) {
                    setFilters({ ...filters, dateAsc: false });
                } else if (filters.filterDate && !filters.dateAsc) {
                    setFilters({ ...filters, filterDate: false, dateAsc: true });
                } else {
                    setFilters({ ...filters, filterDate: true });
                }
            },
        },
        {
            name: 'TIME',
            isSortible: false,
            icon: getIcon(filters.filterTime, filters.timeAsc),
            changeFilters() {
                if (filters.filterTime && filters.timeAsc) {
                    setFilters({ ...filters, timeAsc: false });
                } else if (filters.filterTime && !filters.timeAsc) {
                    setFilters({ ...filters, filterTime: false, timeAsc: true });
                } else {
                    setFilters({ ...filters, filterTime: true });
                }
            },
        },
        { name: 'FROM' },
        {
            name: 'DURATION',
            isSortible: false,
            icon: getIcon(filters.filterDuration, filters.durationAsc),
            changeFilters() {
                if (filters.filterDuration && filters.durationAsc) {
                    setFilters({ ...filters, durationAsc: false });
                } else if (filters.filterDuration && !filters.durationAsc) {
                    setFilters({ ...filters, filterDuration: false, durationAsc: true });
                } else {
                    setFilters({ ...filters, filterDuration: true });
                }
            },
        },
        // { name: 'RECORDING' },
        { name: 'Summary' },
        { name: 'TRANSCRIPT' },
        { name: 'ACTIONS' },
    ];

    const mobileVersion = [
        {
            name: 'By day',
            isSortible: true,
            icon: getIcon(filters.filterDate, filters.dateAsc),
            changeFilters() {
                if (filters.filterDate && filters.dateAsc) {
                    setFilters({ ...filters, dateAsc: false });
                } else if (filters.filterDate && !filters.dateAsc) {
                    setFilters({ ...filters, filterDate: false, dateAsc: true });
                } else {
                    setFilters({ ...filters, filterDate: true });
                }
            },
        },
        {
            name: 'By time',
            isSortible: true,
            icon: getIcon(filters.filterTime, filters.timeAsc),
            changeFilters() {
                if (filters.filterTime && filters.timeAsc) {
                    setFilters({ ...filters, timeAsc: false });
                } else if (filters.filterTime && !filters.timeAsc) {
                    setFilters({ ...filters, filterTime: false, timeAsc: true });
                } else {
                    setFilters({ ...filters, filterTime: true });
                }
            },
        },

        {
            name: 'By duration',
            isSortible: true,
            icon: getIcon(filters.filterDuration, filters.durationAsc),
            changeFilters() {
                if (filters.filterDuration && filters.durationAsc) {
                    setFilters({ ...filters, durationAsc: false });
                } else if (filters.filterDuration && !filters.durationAsc) {
                    setFilters({ ...filters, filterDuration: false, durationAsc: true });
                } else {
                    setFilters({ ...filters, filterDuration: true });
                }
            },
        },
    ];

    if (isMobile) {
        return mobileVersion;
    } else {
        return desktopVersion;
    }
}

export function useGetKnowladgeTh() {
    const desktopVersion = [
        {
            name: 'Source Type',
        },
        {
            name: 'Text',
        },
        { name: 'Date Created' },
        { name: 'Active' },
        { name: 'Actions' },
    ];

    return desktopVersion;
}

export const checkFileSize = (file) => {
    const fileSize = file.size;
    const maxSize = 1000 * 1024 * 1024;

    if (fileSize > maxSize) {
        return false;
    }

    return true;
};

export function useGetFilters(filters, setFilters, setTimeValue, selectPeriodText, isMobile = false) {
    const { today, yesterday, thisWeek, lastWeek, thisMonth, lastMonth } = useGetDate();

    const desktopVersion = [
        {
            name: 'All',
            value: 1,
            changeFilters: () => {
                setTimeValue(1);
                setFilters({ ...filters, startDatetime: null, endDatetime: null });
            },
        },
        {
            name: 'Today',
            value: 2,
            changeFilters: () => {
                setTimeValue(2);
                setFilters({
                    ...filters,
                    startDatetime: today.startOfToday,
                    endDatetime: today.endOfToday,
                });
            },
        },
        {
            name: 'Yesterday',
            value: 3,
            changeFilters: () => {
                setTimeValue(3);
                setFilters({
                    ...filters,
                    startDatetime: yesterday.startOfYesterday,
                    endDatetime: yesterday.endOfYesterday,
                });
            },
        },
        {
            name: 'This week',
            value: 4,
            changeFilters: () => {
                setTimeValue(4);
                setFilters({
                    ...filters,
                    startDatetime: thisWeek.startOfWeek,
                    endDatetime: thisWeek.endOfWeek,
                });
            },
        },
        {
            name: 'Last week',
            value: 5,
            changeFilters: () => {
                setTimeValue(5);
                setFilters({
                    ...filters,
                    startDatetime: lastWeek.startOfLastWeek,
                    endDatetime: lastWeek.endOfLastWeek,
                });
            },
        },
        {
            name: 'This month',
            value: 6,
            changeFilters: () => {
                setTimeValue(6);
                setFilters({
                    ...filters,
                    startDatetime: thisMonth.startOfMonth,
                    endDatetime: thisMonth.endOfMonth,
                });
            },
        },
        {
            name: 'Last month',
            value: 7,
            changeFilters: () => {
                setTimeValue(7);
                setFilters({
                    ...filters,
                    startDatetime: lastMonth.startOfLastMonth,
                    endDatetime: lastMonth.endOfLastMonth,
                });
            },
        },
        {
            name: selectPeriodText,
            value: 8,
        },
    ];

    const mobileVersion = [
        {
            name: 'All',
            value: 1,
            changeFilters: () => {
                setFilters({ ...filters, startDatetime: null, endDatetime: null });
            },
        },
        {
            name: 'Today',
            value: 2,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: today.startOfToday,
                    endDatetime: today.endOfToday,
                });
            },
        },
        {
            name: 'Yesterday',
            value: 3,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: yesterday.startOfYesterday,
                    endDatetime: yesterday.endOfYesterday,
                });
            },
        },
        {
            name: 'This week',
            value: 4,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: thisWeek.startOfWeek,
                    endDatetime: thisWeek.endOfWeek,
                });
            },
        },
        {
            name: 'Last week',
            value: 5,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: lastWeek.startOfLastWeek,
                    endDatetime: lastWeek.endOfLastWeek,
                });
            },
        },
        {
            name: 'This month',
            value: 6,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: thisMonth.startOfMonth,
                    endDatetime: thisMonth.endOfMonth,
                });
            },
        },
        {
            name: 'Last month',
            value: 7,
            changeFilters: () => {
                setFilters({
                    ...filters,
                    startDatetime: lastMonth.startOfLastMonth,
                    endDatetime: lastMonth.endOfLastMonth,
                });
            },
        },
    ];

    if (isMobile) {
        return mobileVersion;
    } else {
        return desktopVersion;
    }
}

export function useThrottle(callback, delay) {
    const timeoutRef = useRef(null);

    const throttledCallback = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            callback();
        }, delay);
    }, [callback, delay]);

    return throttledCallback;
}

export function useClickHandler(firstState, secondState, thirdState) {
    return function onClickHandler(e) {
        e.stopPropagation();
        firstState(false);
        secondState(false);
        thirdState((prev) => !prev);
    };
}

export function useScrollEvent() {
    const [isVisibleTopButton, setIsVisibleTopButton] = useState(false);

    useEffect(() => {
        let prevScrollY = window.scrollY;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > prevScrollY || !currentScrollY) {
                setIsVisibleTopButton(false);
            } else if (currentScrollY < prevScrollY) {
                setIsVisibleTopButton(true);
            }

            prevScrollY = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return [isVisibleTopButton];
}

export function useGetQuestionInfo(questions, questionNumber) {
    const questionInfo = useMemo(() => {
        const targetQuestion = questions.find((question) => question.page === questionNumber);
        return {
            questionText: targetQuestion?.questionText ?? '',
            answerOptions: targetQuestion?.choices ?? [],
            questionId: targetQuestion?.id ?? null,
            answer: targetQuestion?.answerValue ?? '',
        };
    }, [questions, questionNumber]);

    return questionInfo;
}

export function useGetProfileMenu(isMobile) {
    return [
        { name: 'Personal Information', path: isMobile ? PATHS.CHANGE_INFO : PATHS.PROFILE_INFO },
        { name: 'Password', path: PATHS.PROFILE_PASSWORD },
        { name: 'API Key', path: PATHS.API_KEY },
    ];
}
